import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Content, Dialog, DialogContainer, Divider, Heading, ProgressCircle, Text } from '@adobe/react-spectrum';
import { fetchMatchDetail } from '../../../../data/redux/slices/upgradesSlice';
import { humanizeUpgradesEnum } from './labelUtils';
import './UpgradelizerMatchDetailDialog.css';

/**
 * Full-match-list dialog for one (cardId, category) pair. Driven by the
 * `/upgrades/match-detail` endpoint. Opens when the user clicks the
 * "See all matches" affordance on a tile whose `matchesTruncated` is
 * true on the main /upgrades response.
 */
export function UpgradelizerMatchDetailDialog({ cardId, category, cardName, onClose }) {
    const dispatch = useDispatch();
    const key = cardId && category ? `${cardId}::${category}` : null;
    const detail = useSelector((state) => (key ? state.upgrades.matchDetail[key] : null));

    useEffect(() => {
        if (!cardId || !category) return;
        dispatch(fetchMatchDetail({ cardId, category, isReload: true }));
    }, [cardId, category, dispatch]);

    if (!cardId || !category) return null;

    const items = detail?.items || [];
    const isFetching = detail?.isFetching;
    const error = detail?.error;
    const hasMore = detail?.cursor != null;
    const total = detail?.matchCount ?? items.length;

    const loadMore = () => {
        if (isFetching || !hasMore) return;
        dispatch(
            fetchMatchDetail({
                cardId,
                category,
                cursor: detail.cursor,
                isReload: false,
            })
        );
    };

    return (
        <DialogContainer isDismissable onDismiss={onClose}>
            <Dialog UNSAFE_className="upgMatchDetailDialog" height="calc(100vh - 120px)">
                <Heading>
                    <span className="upgMatchDetailEyebrow">All matches · {category}</span>
                    <span className="upgMatchDetailTitle">{cardName || cardId}</span>
                    <span className="upgMatchDetailCount">
                        {total} match{total === 1 ? '' : 'es'}
                    </span>
                </Heading>
                <Divider />
                <Content>
                    {error ? (
                        <div className="upgMatchDetailError">
                            <Text>{error}</Text>
                        </div>
                    ) : items.length === 0 && isFetching ? (
                        <div className="upgMatchDetailLoader">
                            <ProgressCircle isIndeterminate aria-label="Loading matches" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="upgMatchDetailEmpty">
                            <Text>No matches found.</Text>
                        </div>
                    ) : (
                        <div className="upgMatchDetailList">
                            {items.map((match, idx) => (
                                <MatchDetailRow key={idx} match={match} />
                            ))}
                            {hasMore && (
                                <button
                                    type="button"
                                    className="upgMatchDetailLoadMore"
                                    onClick={loadMore}
                                    disabled={isFetching}
                                >
                                    {isFetching ? (
                                        <ProgressCircle isIndeterminate size="S" aria-label="Loading" />
                                    ) : (
                                        'Load more matches'
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </Content>
            </Dialog>
        </DialogContainer>
    );
}

function MatchDetailRow({ match }) {
    const trigger = match?.trigger || {};
    const result = match?.result || {};
    const scopeBadge = match?.actionScope && match.actionScope !== 'self' ? match.actionScope : null;
    return (
        <div className="upgMatchDetailRow">
            <div className="upgMatchDetailRowHead">
                <span className="upgMatchDetailEffect">{humanizeUpgradesEnum(match?.effectType || 'match')}</span>
                {scopeBadge && <span className="upgMatchDetailScope">{humanizeUpgradesEnum(scopeBadge)}</span>}
                <span className="upgMatchDetailScore" title="Match score">
                    ★ {Number.isFinite(match?.scoreRating) ? match.scoreRating.toFixed(1) : match?.score ?? '—'}
                </span>
                {Number.isFinite(match?.maxScore) && <span className="upgMatchDetailMaxScore">/ {match.maxScore}</span>}
            </div>
            {match?.conditionText && <div className="upgMatchDetailCondition">"{match.conditionText}"</div>}
            <div className="upgMatchDetailParts">
                <PartBlock title="Trigger" part={trigger} />
                <PartBlock title="Result" part={result} />
            </div>
        </div>
    );
}

function PartBlock({ title, part }) {
    const actor = part?.actorType;
    const depActor = part?.dependencyActorType;
    const actorActions = part?.actorActions || [];
    const depActions = part?.dependencyActions || [];
    const cardActions = part?.cardActions || [];
    const zonesFrom = part?.zonesFrom || [];
    const zonesTo = part?.zonesTo || [];
    const qualifiers = part?.qualifiers || [];
    const hasAny =
        actor ||
        depActor ||
        actorActions.length ||
        depActions.length ||
        cardActions.length ||
        zonesFrom.length ||
        zonesTo.length ||
        qualifiers.length;
    if (!hasAny) return null;
    return (
        <div className="upgMatchDetailPart">
            <div className="upgMatchDetailPartTitle">{title}</div>
            <dl className="upgMatchDetailPartList">
                <PartLine label="Player" value={actor} />
                <PartLine label="Dep. player" value={depActor} />
                <PartLine label="Player actions" value={actorActions} />
                <PartLine label="Dep. actions" value={depActions} />
                <PartLine label="Card actions" value={cardActions} />
                <PartLine label="Zones from" value={zonesFrom} />
                <PartLine label="Zones to" value={zonesTo} />
                {qualifiers.length > 0 && (
                    <>
                        <dt>Qualifiers</dt>
                        <dd>
                            {qualifiers.map((q, i) => (
                                <span key={i} className="upgMatchDetailQualifier">
                                    {humanizeUpgradesEnum(q.kind)}: [
                                    {(q.values || []).map(humanizeUpgradesEnum).join(', ')}]
                                </span>
                            ))}
                        </dd>
                    </>
                )}
            </dl>
        </div>
    );
}

function PartLine({ label, value }) {
    if (value == null || value === '') return null;
    if (Array.isArray(value) && value.length === 0) return null;
    const display = Array.isArray(value) ? value.map(humanizeUpgradesEnum).join(', ') : humanizeUpgradesEnum(value);
    return (
        <>
            <dt>{label}</dt>
            <dd>{display}</dd>
        </>
    );
}
